import React, { useState, useContext, useEffect, ChangeEvent } from "react";
import { AuthContext } from "../contexts/auth";
import { ClockIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { deleteBreakTimeSlot, updateBreakTimeSlot, createBreakTimeSlot } from "../services/api_working_hours";
import { validateBreak, validateBreaks } from "../utils/timeslots";
import Swal from "sweetalert2";
import { formatTime } from "../utils/timeslots";
import { estraiMessaggiErrore } from '../services/utils';
import LocalBarOutlinedIcon from '@mui/icons-material/LocalBarOutlined';



const EditBreaks = ({ timeSlotEditBreak, handleAddPause, creatingPause, onCancelCreatingPause, onUpdatePauseTimes, allowHiddenBreaks }) => {
    const { token } = useContext(AuthContext);
    const [editingPauseId, setEditingPauseId] = useState(null);
    const [pauseList, setPauseList] = useState(timeSlotEditBreak?.breaks?.map(pause => ({
        ...pause,
        start_at: formatTime(pause?.start_at),
        end_at: formatTime(pause?.end_at),
        is_visible: pause?.is_visible,
    })));
    const [newBreakData, setNewBreakData] = useState({ start_at: '', end_at: '', is_visible: true });
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [originalPauseData, setOriginalPauseData] = useState(null);
    const [visibilityLoading, setVisibilityLoading] = useState({});
    const [pendingPauseList, setPendingPauseList] = useState(null);


    
    useEffect(() => {
        if(creatingPause){
            setEditingPauseId(null);
        }
    }, [creatingPause]);

    useEffect(() => {
        if (pendingPauseList) {
            onUpdatePauseTimes?.(pendingPauseList);
            setPendingPauseList(null);
        }
    }, [pendingPauseList, onUpdatePauseTimes]);
    
    const handleEditClick = (pauseId) => {
        const pause = pauseList.find(p => p.id === pauseId);
        setOriginalPauseData({ ...pause });
        setEditingPauseId(pauseId);
    };


    const handleCancelEdit = () => {
        if (originalPauseData) {
            const updated = pauseList.map(p =>
                p.id === originalPauseData.id ? { ...originalPauseData } : p
            );
            setPauseList(updated);
        }
        setOriginalPauseData(null);
        setEditingPauseId(null);
        setError('');
    };

    const handleInputChange = (key, value, id) => {
        const formattedValue = value.replace(/\D/g, '');
        const limitedValue = formattedValue.slice(0, 4);
    
        let formattedTime = limitedValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");

        if (formattedValue.length <= 2 && value.length < formattedTime.length) {
            formattedTime = formattedTime.replace(":", "");
        }
        
        const hours = parseInt(formattedTime.split(":")[0], 10);
        const minutes = parseInt(formattedTime.split(":")[1] || "0", 10);

        if (hours === 24 || (hours === 23 && minutes > 59)) {
            setError("L'orario massimo consentito è 23:59.");
            return;
        }
      
        if(id) {
            setPauseList((prevData) =>
                prevData.map((pause) => {
                    if (id !== pause.id) {
                        return pause;
                    }
                    return { ...pause, [key]: formattedTime };
                })
            );
            return;
        }
      
        setNewBreakData((prevData) => ({
            ...prevData,
            [key]: formattedTime,
        }));
    };    


    const handleBreakTimeBlur = (index, field) => {
        let formattedTime = index === null ? newBreakData[field] : pauseList[index][field];
   
        if (typeof formattedTime === 'string') {
            formattedTime = formattedTime.replace(/\D/g, '');

            while (formattedTime.length < 4) {
            formattedTime += '0';
            }

            formattedTime = formattedTime.slice(0, 4);

            const hours = formattedTime.slice(0, 2);
            const minutes = formattedTime.slice(2);
            formattedTime = `${hours}:${minutes}`;

            const isValidTime = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formattedTime);

            if (index === null) {
                setNewBreakData(prev => ({
                    ...prev,
                    [field]: isValidTime ? formattedTime : '',
                }));
            } else {
                const updated = [...pauseList];
                updated[index][field] = isValidTime ? formattedTime : '';
                setPauseList(updated);
            }
        }
    };

    const handleUpdateBreakTime = async (pause) => {
        try {
            setIsSaving(true);

            if (!pause.start_at || !pause.end_at) {
                setError('Inserire entrambi gli orari');
                return;
            }

            if (pause.id === undefined) {
                console.error('ID della pausa non trovato');
                return;
            }

            try {
                validateBreaks(timeSlotEditBreak.start_at, timeSlotEditBreak.end_at, pauseList);
              }
              catch (error) {
                setError(error.message)
                return;
              }

            await updateBreakTimeSlot(
                token,
                pause.id,
                { start_at: pause.start_at, end_at: pause.end_at, is_visible: !!pause.is_visible },
                timeSlotEditBreak.id
            );

            const next = pauseList.map(p =>
            p.id === pause.id ? { ...p, start_at: pause.start_at, end_at: pause.end_at, is_visible: !!pause.is_visible } : p
            );

            setPauseList(next);

            setPendingPauseList(next);

            setEditingPauseId(null);
            setError('');
            Swal.fire({
                position: "center",
                icon: "success",
                title: "Salvato!",
                showConfirmButton: false,
                timer: 1500
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: estraiMessaggiErrore?.(error) || 'Errore di aggiornamento.' 
            });
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        const initialPauseList = (timeSlotEditBreak?.breaks ?? []).map(p => ({
            ...p,
            start_at: formatTime(p?.start_at),
            end_at:   formatTime(p?.end_at),
            is_visible: p?.is_visible ?? true,
        }));
        setPauseList(initialPauseList);
    }, [timeSlotEditBreak]);

    const handleDeleteBreak = async (breakId) => {
        try {
            await deleteBreakTimeSlot(token, breakId);
            const newPauseList = pauseList.filter(pause => pause.id !== breakId);
            setPauseList(newPauseList);
            
            if (onUpdatePauseTimes) {
                onUpdatePauseTimes(newPauseList);
            }

        } catch (error) {
            console.error('Errore durante l\'eliminazione della pausa:', error);
        }
    };
    
    const handleCreateBreak = async () => {
        try {
            setIsSaving(true);

            if (!newBreakData.start_at || !newBreakData.end_at) {
                setError('Inserire entrambi gli orari');
                return;
            }

            try {
                validateBreak(timeSlotEditBreak.start_at, timeSlotEditBreak.end_at, newBreakData, pauseList);
                validateBreaks(timeSlotEditBreak.start_at, timeSlotEditBreak.end_at, pauseList);
            }
            catch(error) {
                setError(error.message);
                return;
            }

            const createdBreak = await createBreakTimeSlot(
                token,
                { start_at: newBreakData.start_at, end_at: newBreakData.end_at, is_visible: !!(newBreakData.is_visible) },
                timeSlotEditBreak.id
            );
    
            const updatedPauseList = [...pauseList, createdBreak];
            setPauseList(updatedPauseList);


            onUpdatePauseTimes?.(updatedPauseList); 
    
            if (onUpdatePauseTimes) {
                onUpdatePauseTimes(updatedPauseList);
            }
    
            setNewBreakData({ start_at: '', end_at: '', is_visible: true });
            onCancelCreatingPause();
            setError('');
        } catch (error) {
            console.error('Errore durante la creazione della pausa:', error);
            setError('Si è verificato un errore durante la creazione della pausa.');
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleCancelClick = () => {
        if (onCancelCreatingPause) {
            onCancelCreatingPause();
            setNewBreakData({ start_at: '', end_at: '' });
            setError('');
        }
    };

    const sortByStartTime = (a, b) => {
        const startTimeA = a.start_at;
        const startTimeB = b.start_at;
        
        if (startTimeA < startTimeB) {
          return -1;
        }
        if (startTimeA > startTimeB) {
          return 1;
        }
        return 0;
    };

    return (
        <>
            {error && (
                <p className="text-red-500 text-sm justify-center flex mt-3">{error}</p>
            )}
            <div className="mt-4">
                {creatingPause && (
                    <div className="mt-4 w-full">
                        <div className="grid grid-cols-12">

                            {/* START */}
                            <div className="col-span-6 sm:col-span-4 flex items-center justify-start">
                                <span className="text-sm font-medium text-gray-700 flex items-center px-4">
                                    <LocalBarOutlinedIcon className="!h-[17px] !w-[17px] mr-1" />
                                    Dalle ore:
                                </span>

                                <div className="relative inline-block">
                                    <input
                                        type="text"
                                        placeholder="hh:mm"
                                        value={newBreakData.start_at}
                                        onChange={(e) => handleInputChange("start_at", e.target.value)}
                                        onBlur={() => handleBreakTimeBlur(null, "start_at")}
                                        className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:border-indigo-500 block w-24 shadow-md sm:text-sm"
                                    />
                                    <ClockIcon className="w-4 h-4 absolute top-1/2 right-2 -translate-y-1/2" />
                                </div>
                            </div>

                            {/* END */}
                            <div className="col-span-6 sm:col-span-4 flex items-center justify-start">
                                <span className="text-sm font-medium text-gray-700 flex items-center px-4">
                                    <LocalBarOutlinedIcon className="!h-[17px] !w-[17px] mr-1" />
                                    Alle ore:
                                </span>

                                <div className="relative inline-block">
                                    <input
                                        type="text"
                                        placeholder="hh:mm"
                                        value={newBreakData.end_at}
                                        onChange={(e) => handleInputChange("end_at", e.target.value)}
                                        onBlur={() => handleBreakTimeBlur(null, "end_at")}
                                        className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-24 shadow-md sm:text-sm"
                                    />
                                    <ClockIcon className="w-4 h-4 absolute top-1/2 right-2 -translate-y-1/2" />
                                </div>
                            </div>

                            {/* ACTIONS */}
                            <div className="flex items-center justify-center gap-3 pt-3 col-span-12 sm:col-span-4 flex-wrap">
                                {allowHiddenBreaks && (
                                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        checked={!!(newBreakData.is_visible ?? true)}
                                        onChange={(e) =>
                                            setNewBreakData({
                                            ...newBreakData,
                                            is_visible: e.target.checked,
                                            })
                                        }
                                    />
                                    <span className="text-xs font-medium uppercase">Visibile</span>
                                </label>
                                )}

                                <button
                                    className="bg-indigo-600 text-white text-xs px-3 py-1 rounded-md hover:bg-indigo-500 font-semibold uppercase disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                    onClick={handleCreateBreak}
                                    disabled={isSaving}
                                >
                                    {isSaving ? "Salvataggio..." : "Salva"}
                                </button>

                                <button
                                    className="bg-gray-300 text-gray-700 text-xs px-3 py-1 rounded-md hover:bg-gray-400 font-semibold uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                    onClick={handleCancelClick}
                                >
                                    Annulla
                                </button>
                            </div>
                        </div>

                            {/* Divider solo mobile sotto la creazione */}
                            <div className="block sm:hidden mt-4">
                            <div className="border-b border-gray-200 mx-4"></div>
                        </div>
                    </div>
                )}
                {[...pauseList]
                    .sort(sortByStartTime)
                    .map((pause, index) => {
                        const isEditing = editingPauseId === pause.id;
                        const editingPause = pause;
                        const hasNext = index < pauseList.length - 1;
                        
                    return (
                        <div key={pause.id} className="mt-4 w-full">
                            <div className="grid grid-cols-12">
                            
                                {/* START */}
                                <div className="col-span-6 sm:col-span-4 flex items-center justify-start">
                                    <span className="text-sm font-medium text-gray-700 flex items-center px-4">
                                        <LocalBarOutlinedIcon style={{ height: '17px', width: '17px', marginRight: '4px' }} />
                                        Dalle ore:
                                    </span>

                                    <div className="relative inline-block">
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                placeholder="hh:mm"
                                                value={pause.start_at}
                                                onChange={(e) => handleInputChange('start_at', e.target.value, pause.id)}
                                                onBlur={() => handleBreakTimeBlur(index, 'start_at')}
                                                className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:border-indigo-500 block w-24 shadow-md sm:text-sm"
                                            />
                                        ) : (
                                            <span className="mt-1 p-1 pl-4 rounded-md text-left block w-24 shadow-md sm:text-sm">
                                                {formatTime(pause?.start_at)}
                                            </span>
                                        )}
                                        <ClockIcon className="w-4 h-4 absolute top-1/2 right-2 -translate-y-1/2" />
                                    </div>
                                </div>

                                {/* END */}
                                <div className="col-span-6 sm:col-span-4 flex items-center justify-start">
                                    <span className="text-sm font-medium text-gray-700 flex items-center px-4">
                                        <LocalBarOutlinedIcon style={{ height: '17px', width: '17px', marginRight: '4px' }} />
                                        Alle ore:
                                    </span>

                                    <div className="relative inline-block">
                                        {isEditing ? (
                                            <input
                                            type="text"
                                            placeholder="hh:mm"
                                            value={pause.end_at}
                                            onChange={(e) => handleInputChange('end_at', e.target.value, pause.id)}
                                            onBlur={() => handleBreakTimeBlur(index, 'end_at')}
                                            className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-24 shadow-md sm:text-sm"
                                            />
                                        ) : (
                                            <span className="mt-1 p-1 pl-4 text-left rounded-md block w-24 shadow-md sm:text-sm">
                                            {formatTime(pause?.end_at)}
                                            </span>
                                        )}
                                        <ClockIcon className="w-4 h-4 absolute top-1/2 right-2 -translate-y-1/2" />
                                    </div>
                                </div>

                                {/* ACTIONS */}
                                {!creatingPause && (
                                    <div className="flex items-center justify-center gap-3 pt-3 col-span-12 sm:col-span-4 flex-wrap">
                                        {isEditing ? (
                                            <>
                                                {allowHiddenBreaks && (
                                                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                            checked={!!editingPause.is_visible}
                                                            onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setPauseList(prev =>
                                                                prev.map(p =>
                                                                p.id === pause.id ? { ...p, is_visible: checked } : p
                                                                )
                                                            );
                                                            }}
                                                        />
                                                        <span className="text-xs font-medium uppercase">Visibile</span>
                                                    </label>
                                                )}

                                                <button
                                                    className="bg-indigo-600 text-white text-xs px-3 py-1 rounded-md hover:bg-indigo-500 font-semibold uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                                    onClick={() => handleUpdateBreakTime(editingPause)}
                                                    disabled={isSaving}
                                                >
                                                    {isSaving ? 'Salvataggio...' : 'Salva'}
                                                </button>

                                                <button
                                                    className="bg-gray-300 text-gray-700 text-xs px-3 py-1 rounded-md hover:bg-gray-400 font-semibold uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                                    onClick={handleCancelEdit}
                                                >
                                                    Annulla
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                {allowHiddenBreaks && (
                                                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                            checked={pause.is_visible ?? true}
                                                            onChange={(e) =>
                                                            handleUpdateBreakTime({
                                                                ...pause,
                                                                is_visible: e.target.checked,
                                                            })
                                                            }
                                                            disabled={visibilityLoading?.[pause.id]}
                                                        />
                                                        <span className="text-xs font-medium uppercase">Visibile</span>
                                                    </label>
                                                )}

                                                <button
                                                    className="text-gray-700 px-1"
                                                    onClick={() => handleEditClick(pause.id)}
                                                >
                                                    <PencilSquareIcon className="h-6 w-6 hover:text-indigo-500" />
                                                </button>

                                                <button
                                                    onClick={() => handleDeleteBreak(pause.id ?? 0)}
                                                    className="text-gray-700 px-1"
                                                >
                                                    <TrashIcon className="h-6 w-6 hover:text-red-500" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* DIVIDER SOLO MOBILE (non tocca i bordi) */}
                            {hasNext && (
                                <div className="block sm:hidden mt-4">
                                    <div className="border-b border-gray-200 mx-4"></div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
        
    );
};

export default EditBreaks;

